<template>
  <BaseCard
    title="Commission by Status"
    class="h-full"
  >
    <template #content>
         <div class="flex flex-col gap-4 min-h-fit w-full">
        <div class="flex flex-col gap-4 w-full">
          <div v-if="isLoading">
            <Skeleton
              height="400px"
              width="100%"
            />
          </div>
          <div
            v-else-if="commissionByStatus.length > 0"
            class="flex flex-col gap-4 w-full  rounded-lg"
          >
            <ClientOnly>
              <VChart
                class="w-full min-h-[400px] max-h-[500px]"
                :option="option"
                autoresize
              />
              <template #fallback>
                <div class="text-center text-gray-500 p-4">
                  Loading chart...
                </div>
              </template>
            </ClientOnly>
          </div>

          <div
            v-else
            class="flex flex-col items-center justify-center p-12 rounded-lg"
          >
            <Icon
              name="mdi:chart-bar"
              size="48"
              class="text-gray-300 mb-4"
            />
            <p class="text-gray-500 text-center">
              No commission data by status available
            </p>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import type { ComposeOption } from 'echarts/core'
import type {
  BarSeriesOption,
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
  GridComponentOption,
} from 'echarts'
import { useDashboard } from '~/composables/useDashboardCA'
import { useUtils } from '~/composables/useUtils'
import type { ContractStatus } from '@prisma/client'

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
])

type EChartsOption = ComposeOption<
  | BarSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | GridComponentOption
>

interface CommissionByStatus {
  status: ContractStatus
  commission: number
  contractCount: number
}

interface CommissionAnalyticsResponse {
  summary: unknown
  commissionByAgent: unknown[]
  commissionByMonth: unknown[]
  commissionByStage: unknown[]
  commissionByStatus: CommissionByStatus[]
  officeVsAgentBreakdown: unknown
}

const { getCommissionAnalytics } = useDashboard()
const { formatToDollarsWithoutCents } = useUtils()

const isLoading = ref(true)
const commissionByStatus = ref<CommissionByStatus[]>([])

const formatCurrency = (value: number): string => {
  if (!value || value === 0) return '$0'
  const formatted = formatToDollarsWithoutCents(value)
  return formatted.formatted || '$0'
}

const getPrimaryColor = () => {
  return getComputedStyle(document.documentElement)
    ?.getPropertyValue('--p-primary-200')
    ?.trim() || '#97BA9F'
}

const option = computed<EChartsOption>(() => {
  if (commissionByStatus.value.length === 0) {
    return {
      title: {
        text: 'No Data',
        left: 'center',
        top: 'middle',
        textStyle: {
          color: '#999',
        },
      },
    }
  }

  const primaryColor = getPrimaryColor()
  const labels = commissionByStatus.value.map(item => item.status)
  const data = commissionByStatus.value.map(item => item.commission)

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: '#fff',
      },
      formatter: (params: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const param = Array.isArray(params) ? params[0] : params as any
        const value = param?.value
        if (value === null || value === undefined) return ''
        return `${param.name}<br/>${formatCurrency(value)}`
      },
    },
    grid: {
      left: '10%',
      right: '5%',
      bottom: '25%',
      top: '5%',
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        interval: 0,
        fontSize: 10,
        color: '#666',
        width: 80,
        formatter: (value: string) => {
          if (value.length > 15) {
            return value.substring(0, 15) + '...'
          }
          return value
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#e0e0e0',
        },
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) => {
          if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`
          }
          else if (value >= 1000) {
            return `$${(value / 1000).toFixed(1)}K`
          }
          return `$${value}`
        },
        color: '#666',
      },
    },
    series: [
      {
        name: 'Commission',
        type: 'bar',
        data,
        barWidth: '80%',
        itemStyle: {
          color: primaryColor,
          borderRadius: [4, 4, 0, 0],
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params: unknown) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const value = (params as any)?.value
            if (value >= 1000000) {
              return `$${(value / 1000000).toFixed(1)}M`
            }
            else if (value >= 1000) {
              return `$${(value / 1000).toFixed(1)}K`
            }
            return formatCurrency(value)
          },
          fontSize: 10,
          color: '#333',
        },
      },
    ],
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut',
  }
})

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getCommissionAnalytics()
    const data = response?.data as CommissionAnalyticsResponse

    if (data) {
      commissionByStatus.value = data.commissionByStatus || []
    }
  }
  catch (error) {
    console.error('Error fetching commission analytics chart:', error)
    commissionByStatus.value = []
  }
  finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getData()
})
</script>
